import { z } from 'zod';

const envSchema = z.object({
  VITE_API_BASE_URL: z
    .string()
    .url()
    .refine((u) => !/\/api\/v\d+\/?$/.test(u), {
      message:
        'VITE_API_BASE_URL no debe incluir el prefijo /api/vN — el versionado por URI lo aplica el backend (ADR-004) y los paths del spec OpenAPI ya lo incluyen.',
    }),
  VITE_APP_NAME: z.string().min(1).default('ANI Control de Incidencias'),
  VITE_SESSION_INACTIVITY_MINUTES: z.coerce.number().int().positive().default(30),
  VITE_ENABLE_DEVTOOLS: z
    .union([z.boolean(), z.string()])
    .transform((v) => (typeof v === 'string' ? v === 'true' : v))
    .default(false),
});

export type AppConfig = {
  apiBaseUrl: string;
  appName: string;
  sessionInactivityMinutes: number;
  enableDevtools: boolean;
};

let cachedConfig: AppConfig | null = null;

/**
 * Lectura tipada de variables de entorno. Si falta una variable obligatoria,
 * la app no debe arrancar — se lanza error en bootstrap.
 */
export function getConfig(): AppConfig {
  if (cachedConfig) return cachedConfig;

  const parsed = envSchema.safeParse(import.meta.env);
  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((i) => `  - ${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('\n');
    throw new Error(
      `Variables de entorno inválidas o faltantes. Revise .env / .env.example:\n${issues}`,
    );
  }

  cachedConfig = {
    apiBaseUrl: parsed.data.VITE_API_BASE_URL,
    appName: parsed.data.VITE_APP_NAME,
    sessionInactivityMinutes: parsed.data.VITE_SESSION_INACTIVITY_MINUTES,
    enableDevtools: parsed.data.VITE_ENABLE_DEVTOOLS,
  };
  return cachedConfig;
}
